// The app shell: the masthead, the two controls beside it, and the view switcher.
//
// The switcher is back, and this time it has somewhere to go. A pair of view buttons shipped
// `hidden` here for weeks, guarding a second view that could only be reached by typing into a
// console; both were deleted in the React migration. These are real links to two real boards,
// and which one a caller lands on is decided by `/api/me` rather than by this component.
//
// GRO-80 emptied the title block. It used to carry four lines of diagnostics -- the owner list,
// the cache time, the build stamp, the mirror's sync schedule -- under a heading read by people
// who do not know what a commit is. Every one of those facts still reaches the page; none of
// them is printed until somebody asks. What is left is a title, a freshness pill and a profile
// button.

import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchAutoSendCount, fetchMe, readViewAs } from '../api/client';
import { useAuthContext } from '../auth/AuthContext';
import { badgeText } from '../lib/sends';
import { useUnreadChatCount } from '../chat/ChatProvider';
import { firstNameFromEmail, viewAsBannerTail, viewAsLine } from '../lib/viewAs';
import { PreviewBanner } from './PreviewBanner';
import { ProfileMenu } from './ProfileMenu';
import { StaleBuildNotice } from './StaleBuildNotice';
import { SyncPill } from './SyncPill';
import { ViewAsSwitcher } from './ViewAsSwitcher';
import { offeredViewTabs, viewIsOffered } from '../lib/viewTabs.ts';
import type { Build, Freshness, Viewer } from '../api/types';

export function PageChrome({
  freshness,
  build,
  viewer: pageViewer,
  isRebuilding = false,
  onRebuild,
  children,
}: {
  freshness: Freshness | null;
  build?: Build;
  // Absent until the first payload lands, which is why the two controls can render without it.
  viewer: Viewer | undefined;
  isRebuilding?: boolean;
  // Only the execution board has a rebuild endpoint behind it, so only it passes this.
  onRebuild?: () => void;
  children: ReactNode;
}) {
  const { email, signOut, getToken } = useAuthContext();
  // The page's own viewer (from its payload) once it has one; until then `/api/me`'s, which answers
  // in milliseconds while the book takes seconds. Same `viewer_payload` on the server, so the tab bar
  // and controls draw once, from the right role, instead of growing when the book lands (GRO-491).
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => fetchMe(getToken) });
  const viewer = pageViewer ?? meQuery.data?.viewer;
  const viewingAs = readViewAs();
  // A Scout chat that finished while the person was on another board (GRO-274).
  const unreadChats = useUnreadChatCount();
  // How many auto-lane sequences wait for review. Only asked of someone who may approve.
  const canReview = Boolean(viewer?.can_approve);
  const sendsCount = useQuery({
    queryKey: ['auto-send-count'],
    queryFn: () => fetchAutoSendCount(getToken),
    enabled: canReview,
    staleTime: 60_000,
  });
  const sendsBadge = canReview ? badgeText(sendsCount.data?.pending_count) : null;
  // Scout, Activity, the accounts tab, All accounts, My Style, then Review and Admin (`lib/viewTabs.ts`).
  const views = offeredViewTabs(viewer);

  return (
    <div className="wrap" id="app-background">
      {/* A Render PR preview: writes off, Postgres in `preview_v2`. Silent everywhere else. */}
      {__MOCK_API__ ? null : <PreviewBanner />}
      {/* Every number on the page is invented when this shows. It is loud on purpose: a
          fixture that reads plausibly is indistinguishable from the real board in a
          screenshot, and one was mistaken for it. `__MOCK_API__` is compiled away in any
          real build, so this cannot reach anybody who is not running the mock themselves. */}
      {__MOCK_API__ ? (
        <div className="mockbanner" role="status">
          <b>Illustrative data.</b> Companies and numbers are invented. Both previews use the same sample book.
        </div>
      ) : null}
      {viewingAs ? (
        <div className="viewasbanner" role="status">
          {viewAsLine(
            firstNameFromEmail(viewingAs),
            viewer?.acting_for ? viewer.role : null,
          )} — {viewAsBannerTail(firstNameFromEmail(viewingAs), viewer?.impersonator)}
        </div>
      ) : null}
      <div className="top">
        <div>
          <p className="eyebrow">
            <span className="mark">Cartesia</span>
            <span className="brandsep" />
            Demand Gen
          </p>
          <h1>
            Outbound <span className="accent">Dashboard</span>
          </h1>
        </div>
        {/* Freshness first, then identity: one is about the page and one is about the reader,
            and the page's own state is the more often glanced at. */}
        <div className="chromebar">
          {viewer && !__MOCK_API__ ? <ViewAsSwitcher viewer={viewer} /> : null}
          <SyncPill freshness={freshness} build={build} />
          {viewer ? (
            <ProfileMenu
              email={email}
              role={viewer.role}
              canRebuild={viewer.can_rebuild}
              isRebuilding={isRebuilding}
              onRebuild={onRebuild}
              onSignOut={signOut}
              showsStyle={viewIsOffered('/settings/style', viewer)}
            />
          ) : null}
        </div>
      </div>

      {/* Silent unless this tab is genuinely running an older bundle than the server. */}
      <StaleBuildNotice build={build} />

      {/* Below the masthead rather than beside the controls: it switches the whole page, so it
          belongs with the page rather than in the row of things that act on it. */}
      <nav className="viewnav" aria-label="Dashboard views">
        {views.map(view => (
          <NavLink
            key={view.to}
            to={view.to}
            title={view.hint}
            end={view.to === '/admin'}
            className={({ isActive }) => 'viewtab' + (isActive ? ' on' : '')}
          >
            {view.label}
            {view.to === '/scout' && unreadChats > 0 ? <span className="chatdot" aria-label={`${unreadChats} new Scout answer${unreadChats === 1 ? '' : 's'}`} /> : null}
            {view.to === '/admin/sends' && sendsBadge ? <span className="tabbadge" aria-label={`${sendsBadge} waiting for review`}>{sendsBadge}</span> : null}
          </NavLink>
        ))}
      </nav>

      <div>{children}</div>
    </div>
  );
}
